/**
 * JiveX skin.
 *
 * Light values are measured from a JiveX screenshot: a cool grey ladder
 * (#1b1f22 .. #ededed, blue channel consistently 2-3 points above red) plus a
 * single warm accent #ffc567. The accent only ever appears on values and
 * highlights, never as a button fill, and only carries dark text -- white on
 * #ffc567 is 1.6:1.
 *
 * Chrome keeps the original JiveX grey (bgDefault), content surfaces use the
 * lighter form panel tone (bgPaper) so the editor stays readable.
 *
 * Dark is a derived counterpart, not measured. The grey ladder is mirrored and
 * the accent is kept identical so both modes share one brand colour.
 */
const jivexSkin = {
  id: 'jivex',
  label: 'JiveX',
  light: {
    primary: '#4f5356',
    primaryHover: '#3c4042',
    primaryActive: '#2b2f31',
    primaryContrast: '#e7e7e7',
    accent: '#ffc567',

    chromeBg: '#1b1f22',
    chromeFg: '#e7e7e7',
    chromeTabInactive: '#4f5356',
    chromeTabInactiveHover: '#5f6467',
    chromeSurface: '#4f5356',

    bgDefault: '#a3a4a6',
    bgPaper: '#ededed',
    bgElevated: '#e7e7e7',
    bgSubtle: '#c5c6c8',
    bgDialogActions: '#c5c6c8',
    bgField: '#fbfbfb',
    textPrimary: '#1b1f22',
    textSecondary: '#4f5356',
    textDisabled: '#8a8b8d',
    divider: '#c0c1c3',
    border: '#767779',

    error: '#b4342a',
    errorContrast: '#ffffff',
    warning: '#d99f31',
    success: '#3f7d4f',
    info: '#4a6b8a',
    danger: '#8f271f',

    statusNew: '#4a6b8a',
    statusNewFg: '#ffffff',
    statusStarted: '#cfe0d3',
    statusStartedFg: '#3f7d4f',
    statusAwaiting: '#d99f31',
    statusAwaitingFg: '#1b1f22',
    statusApproved: '#3f7d4f',
    statusApprovedFg: '#ffffff',
    statusProvisionally: '#6a5a8a',
    statusProvisionallyFg: '#ffffff',
    statusRejected: '#b4342a',
    statusRejectedFg: '#ffffff',
    statusCorrection: '#f6e7c4',
    statusCorrectionFg: '#8a6410',
    statusCorrectionBorder: '#e6cf95',

    emergencyNormal: '#3f7d4f',
    emergencyNormalBg: 'rgba(63, 125, 79, 0.14)',
    emergencyUrgent: '#c07a1e',
    emergencyUrgentBg: 'rgba(192, 122, 30, 0.16)',
    emergencyEmergency: '#b4342a',
    emergencyEmergencyBg: 'rgba(180, 52, 42, 0.14)',
    insurance: '#4a6b8a',
    insuranceBg: 'rgba(74, 107, 138, 0.14)',

    calDuty: '#4a6b8a',
    calOncall: '#c07a1e',
    calVacation: '#3f7d4f',
    calAbsence: '#b4342a',
    calTraining: '#6a5a8a',
    calBlocked: '#4f5356',
    calMaintenance: '#3f7d7d',

    chart1: '#ffc567',
    chart2: '#4a6b8a',
    chart3: '#3f7d4f',
    chart4: '#c07a1e',
    chart5: '#6a5a8a',
    chart6: '#b4342a',
    chart7: '#7f9bb0',
    chart8: '#8a8b8d',
    chart9: '#e6cf95',
    chart10: '#3f7d7d',

    rulerDark: '#e6cf95',
    rulerLight: '#f6e7c4',
    rulerBg: 'transparent',
    marginColor: '#f0dcae',
    marginBg: 'rgba(255, 197, 103, 0.12)',
    marginBorder: '#d99f31',

    chatGroup: '#6a5a8a',
    chatMale: '#4a6b8a',
    chatFemale: '#a15070',
    chatGroupHover: '#54476e',
    chatMaleHover: '#3a556e',
    chatFemaleHover: '#833f5a',

    dbFieldBg: '#f6e7c4',
    scrollbarThumb: '#8a8b8d',
    datagridBg: '#ededed',
    datagridHeader: '#1b1f22',

    demo1: '#b4342a',
    demo2: '#6a5a8a',
    demo3: '#54476e',
    demo4: '#4a6b8a',
    demo5: '#3a7d96',
    demo6: '#3f7d7d',
    demo7: '#c07a1e',
    demo8: '#4f5356',
  },
  dark: {
    primary: '#8a8d8f',
    primaryHover: '#9fa1a3',
    primaryActive: '#b4b6b8',
    primaryContrast: '#1b1f22',
    accent: '#ffc567',

    chromeBg: '#1b1f22',
    chromeFg: '#e7e7e7',
    chromeTabInactive: '#3a3f41',
    chromeTabInactiveHover: '#4f5356',
    chromeSurface: '#3a3f41',

    bgDefault: '#242829',
    bgPaper: '#2f3335',
    bgElevated: '#3a3f41',
    bgSubtle: '#1b1f22',
    bgDialogActions: '#1b1f22',
    bgField: '#3a3f41',
    textPrimary: '#e7e7e7',
    textSecondary: '#a3a4a6',
    textDisabled: '#767779',
    divider: '#3f4446',
    border: '#4f5356',

    error: '#d9564a',
    errorContrast: '#ffffff',
    warning: '#e6b358',
    success: '#5a9a6b',
    info: '#6d8fae',
    danger: '#b4342a',

    statusNew: '#4a6b8a',
    statusNewFg: '#ffffff',
    statusStarted: '#3a5744',
    statusStartedFg: '#c7e0cf',
    statusAwaiting: '#c07a1e',
    statusAwaitingFg: '#1b1f22',
    statusApproved: '#3f7d4f',
    statusApprovedFg: '#ffffff',
    statusProvisionally: '#54476e',
    statusProvisionallyFg: '#ded5ee',
    statusRejected: '#8f271f',
    statusRejectedFg: '#f3cdc9',
    statusCorrection: '#5a4718',
    statusCorrectionFg: '#f0dcae',
    statusCorrectionBorder: '#8a6410',

    emergencyNormal: '#5a9a6b',
    emergencyNormalBg: 'rgba(90, 154, 107, 0.18)',
    emergencyUrgent: '#e6b358',
    emergencyUrgentBg: 'rgba(230, 179, 88, 0.18)',
    emergencyEmergency: '#d9564a',
    emergencyEmergencyBg: 'rgba(217, 86, 74, 0.18)',
    insurance: '#6d8fae',
    insuranceBg: 'rgba(109, 143, 174, 0.18)',

    calDuty: '#6d8fae',
    calOncall: '#e6b358',
    calVacation: '#5a9a6b',
    calAbsence: '#d9564a',
    calTraining: '#8a78ab',
    calBlocked: '#767779',
    calMaintenance: '#54a09b',

    chart1: '#ffc567',
    chart2: '#6d8fae',
    chart3: '#5a9a6b',
    chart4: '#e6b358',
    chart5: '#8a78ab',
    chart6: '#d9564a',
    chart7: '#9fb8c9',
    chart8: '#a3a4a6',
    chart9: '#f0dcae',
    chart10: '#54a09b',

    rulerDark: '#e6cf95',
    rulerLight: '#f6e7c4',
    rulerBg: 'transparent',
    marginColor: '#f0dcae',
    marginBg: 'rgba(255, 197, 103, 0.12)',
    marginBorder: '#d99f31',

    chatGroup: '#8a78ab',
    chatMale: '#6d8fae',
    chatFemale: '#c07490',
    chatGroupHover: '#6a5a8a',
    chatMaleHover: '#4a6b8a',
    chatFemaleHover: '#a15070',

    dbFieldBg: '#4a4232',
    scrollbarThumb: '#767779',
    datagridBg: '#2f3335',
    datagridHeader: '#1b1f22',

    demo1: '#b4342a',
    demo2: '#6a5a8a',
    demo3: '#54476e',
    demo4: '#4a6b8a',
    demo5: '#3a7d96',
    demo6: '#3f7d7d',
    demo7: '#c07a1e',
    demo8: '#4f5356',
  },
};

export default jivexSkin;
